import React, { useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { TemplateVariable } from '../../../../../types/communication';
import { variableToken } from '../../../../../utils/templateVariables';
import ConstantPicker from './ConstantPicker';
import VariablePicker from './VariablePicker';

interface RichTextFieldProps {
    value: string;
    onChange: (html: string) => void;
    variables: TemplateVariable[];
    disabled?: boolean;
}

const QUILL_MODULES = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        [{ size: ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['blockquote'],
        ['link'],
        ['clean'],
    ],
};

const RichTextField: React.FC<RichTextFieldProps> = ({ value, onChange, variables, disabled }) => {
    const quillRef = useRef<ReactQuill>(null);

    const insert = (name: string) => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;
        const token = variableToken(name);
        const range = editor.getSelection(true);
        const index = range ? range.index : editor.getLength();
        editor.insertText(index, token, 'user');
        editor.setSelection(index + token.length, 0, 'user');
    };

    if (disabled) {
        return (
            <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 [&_.ql-container]:border-0 [&_.ql-editor]:text-sm">
                <ReactQuill theme="snow" value={value} readOnly modules={{ toolbar: false }} />
            </div>
        );
    }

    return (
        <div className="rich-text-field [&_.ql-toolbar]:rounded-t-lg [&_.ql-toolbar]:border-gray-300 [&_.ql-container]:rounded-b-lg [&_.ql-container]:border-gray-300 [&_.ql-container]:text-sm [&_.ql-editor]:min-h-[200px] [&_.ql-editor]:max-h-[520px] [&_.ql-editor]:overflow-y-auto">
            <div className="flex justify-end gap-1.5 mb-1">
                <VariablePicker variables={variables} onPick={insert} />
                <ConstantPicker onPick={insert} />
            </div>
            <ReactQuill
                ref={quillRef}
                theme="snow"
                value={value}
                onChange={(html, _delta, source) => {
                    if (source === 'user') onChange(html);
                }}
                modules={QUILL_MODULES}
            />
        </div>
    );
};

export default RichTextField;
